import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleCallbackLoader } from "@/components/GoogleButton";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // AuthCallback runs once; use ref (not state) to survive StrictMode double-invoke.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Emergent Auth returns to `{redirect_url}#session_id=xxx`
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", { session_id: sid });
        // Clear the session_id from the URL fragment right away.
        window.history.replaceState({}, "", window.location.pathname);

        if (data.existing) {
          setSession(data.user, data.token);
          const dest = data.user.role === "merchant" ? "/merchant" : "/driver";
          navigate(dest, { replace: true });
          return;
        }

        // Brand-new email. Route to the pre-chosen signup flow, or the chooser.
        const intent = localStorage.getItem("routa_google_intent");
        localStorage.removeItem("routa_google_intent");
        const gt = encodeURIComponent(data.signup_token);
        if (intent === "driver") {
          navigate(`/apply?gt=${gt}`, { replace: true });
        } else if (intent === "merchant") {
          navigate(`/register?gt=${gt}`, { replace: true });
        } else {
          navigate(`/onboarding?gt=${gt}`, { replace: true });
        }
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || "Google sign-in failed. Please try again.";
        toast.error(msg);
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <GoogleCallbackLoader />;
}
